import { useEffect, useState } from 'react';
import { useUser } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';

export interface UserEducation {
  id: string;
  user_id: string;
  institution: string;
  degree: string;
  field_of_study?: string;
  start_date?: string;
  end_date?: string;
  is_current?: boolean;
  grade?: string;
  activities?: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export const useUserEducations = (userId?: string) => {
  const { user } = useUser();
  const [educations, setEducations] = useState<UserEducation[]>([]);
  const [loading, setLoading] = useState(true);

  const targetUserId = userId || user?.id;

  useEffect(() => {
    if (!targetUserId) {
      setEducations([]);
      setLoading(false);
      return;
    }

    const fetchEducations = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('user_educations')
          .select('*')
          .eq('user_id', targetUserId as any)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching educations:', error);
          return;
        }

        setEducations((data as any) || []);
      } catch (error) {
        console.error('Error fetching educations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEducations();

    // Listen for profile data updates
    const handleProfileUpdate = () => {
      fetchEducations();
    };

    window.addEventListener('profileDataUpdated', handleProfileUpdate);

    return () => {
      window.removeEventListener('profileDataUpdated', handleProfileUpdate);
    };
  }, [targetUserId]);

  const addEducation = async (education: Omit<UserEducation, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!user?.id) return;

    try {
        const { data, error } = await supabase
          .from('user_educations')
          .insert({ 
            user_id: user.id,
            institution: education.institution,
            degree: education.degree,
            field_of_study: education.field_of_study,
            start_date: education.start_date,
            end_date: education.end_date,
            is_current: education.is_current,
            grade: education.grade,
            activities: education.activities,
            description: education.description
          } as any)
          .select()
          .single();

        if (error) throw error;

        setEducations(prev => [(data as any), ...prev]);
      return data;
    } catch (error) {
      console.error('Error adding education:', error);
      throw error;
    }
  };

  const updateEducation = async (id: string, updates: Partial<UserEducation>) => {
    try {
        const { data, error } = await supabase
          .from('user_educations')
          .update(updates as any)
          .eq('id', id as any)
          .select()
          .single();

        if (error) throw error;

        setEducations(prev => prev.map(e => e.id === id ? (data as any) : e));
      return data;
    } catch (error) {
      console.error('Error updating education:', error);
      throw error;
    }
  };

  const deleteEducation = async (id: string) => {
    try {
      const { error } = await supabase
        .from('user_educations')
        .delete()
        .eq('id', id as any);

      if (error) throw error;

      setEducations(prev => prev.filter(e => e.id !== id));
    } catch (error) {
      console.error('Error deleting education:', error);
      throw error;
    }
  };

  return {
    educations,
    loading,
    addEducation,
    updateEducation,
    deleteEducation
  };
};